import { useState } from 'react';
import { useParams } from 'react-router';
import { useFetchMandate } from 'receivable-invoices-js/react/api/queries/fetch-mandate/fetch-mandate';
import {
  useSignMandate,
  SignMandateValidationError,
} from 'receivable-invoices-js/react/api/mutations/sign-mandate';
import { POINTER_TO_FIELD_MAP } from 'receivable-invoices-js/react/constants/mandates';
import { createMandateErrorHandler } from 'receivable-invoices-js/react/utils/mandates';
import type { MandateError } from 'app/react/utils/mandates/error-handler/error-hander';
import type { SignMandateFormData } from './route';

interface UseRouteQueryOptions {
  onSignSuccess?: () => void;
  onSignError?: () => void;
}

export function useRouteQuery(options?: UseRouteQueryOptions) {
  const { onSignSuccess, onSignError } = options || {};
  const { id } = useParams<{ id: string }>();
  const [errorType, setErrorType] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const handleError = (error: unknown) => {
    try {
      const errorHandler = createMandateErrorHandler({ isSuccessPage: false });
      errorHandler(error as MandateError);
    } catch (processedError) {
      const errorMessage =
        processedError instanceof Error ? processedError.message : 'generic_error';
      setErrorType(errorMessage);
      throw processedError;
    }
  };

  const mandateId = id ?? '';
  const {
    data: mandate,
    isLoading: mandateLoading,
    isError: mandateError,
    refetch,
  } = useFetchMandate({ id: mandateId }, { onError: handleError }, { enabled: Boolean(mandateId) });

  const { mutate: signMandate, isPending: isSigningMandate } = useSignMandate({
    onSuccess: () => {
      onSignSuccess?.();
    },
    onError: (error: unknown) => {
      if (error instanceof SignMandateValidationError) {
        const newFieldErrors: Record<string, string[]> = {};

        error.errors.forEach(validationError => {
          const fieldPointer = validationError.source.pointer.split('/').pop();
          const fieldName = fieldPointer
            ? POINTER_TO_FIELD_MAP[fieldPointer] || fieldPointer
            : 'unknown';

          // Only process field errors if we have a valid field pointer
          if (fieldPointer && fieldName !== 'unknown') {
            if (!newFieldErrors[fieldName]) {
              newFieldErrors[fieldName] = [];
            }
            newFieldErrors[fieldName].push(validationError.code);
          }
        });

        // If we have field errors, set them
        const hasFieldErrors = Object.keys(newFieldErrors).length > 0;
        if (hasFieldErrors) {
          setFieldErrors(newFieldErrors);
        } else {
          // If no field errors (like mandate_already_signed)
          onSignError?.();
        }
        return;
      }

      // Handle any other errors
      onSignError?.();
    },
  });

  const clearFieldErrors = () => {
    setFieldErrors({});
  };

  const clearFieldError = (fieldName: string) => {
    setFieldErrors((prev: Record<string, string[]>) => ({
      ...prev,
      [fieldName]: [],
    }));
  };

  const setFieldError = (fieldName: string, errors: string[]) => {
    setFieldErrors((prev: Record<string, string[]>) => ({
      ...prev,
      [fieldName]: errors,
    }));
  };

  const addClientSideErrors = (formData: SignMandateFormData) => {
    const newFieldErrors: Record<string, string[]> = {};

    if (!formData.clientAddress) {
      newFieldErrors.clientAddress = ['required'];
    }
    if (!formData.clientZipCode) {
      newFieldErrors.clientZipCode = ['required'];
    }
    if (!formData.clientCity) {
      newFieldErrors.clientCity = ['required'];
    }
    if (!formData.clientCountryCode) {
      newFieldErrors.clientCountryCode = ['required'];
    }
    if (!formData.clientIban) {
      newFieldErrors.clientIban = ['required'];
    }
    if (formData.clientCountryCode === 'IT' && !formData.clientProvinceCode) {
      newFieldErrors.clientProvinceCode = ['required'];
    }
    if (!formData.termsAndConditions) {
      newFieldErrors.termsAndConditions = ['required'];
    }

    setFieldErrors(newFieldErrors);
    return Object.keys(newFieldErrors).length > 0;
  };

  return {
    errorType,
    isError: mandateError,
    isLoading: mandateLoading,
    mandate,
    refetch,
    // Sign mandate related
    signMandate,
    isSigningMandate,
    fieldErrors,
    clearFieldErrors,
    clearFieldError,
    setFieldError,
    addClientSideErrors,
  };
}
